import { useEffect } from 'react'
import { doc } from 'firebase/firestore'
import type { DocumentReference } from 'firebase/firestore'
import { useLocaleContext } from 'fbtee'
import { db } from '../lib/firebase'
import { setDoc } from '../lib/firestore'
import { log } from '../lib/logger'
import type { UserPreferences } from '@shared/preferences'
import { useAuth } from './useAuth'

const MIRRORED_KEY = 'beacon_mirrored_locale'

function readMirrored(uid: string): string | null {
  try {
    return localStorage.getItem(`${MIRRORED_KEY}:${uid}`)
  } catch {
    return null
  }
}

function writeMirrored(uid: string, locale: string) {
  try {
    localStorage.setItem(`${MIRRORED_KEY}:${uid}`, locale)
  } catch {
    // Without the flag we just write the same locale again next open.
  }
}

/**
 * Mirrors the app language into `users/{uid}/private/preferences.locale` so
 * pushes from Cloud Functions arrive in it. Writes only when this device's
 * language changed since its last mirror; the device a user last opened wins.
 */
export function useLocaleMirror() {
  const { signedInUid } = useAuth()
  const { locale } = useLocaleContext()

  useEffect(() => {
    if (!signedInUid || readMirrored(signedInUid) === locale) return
    const uid = signedInUid
    const ref = doc(db, 'users', uid, 'private', 'preferences') as DocumentReference<UserPreferences>
    setDoc(ref, { locale }, { merge: true })
      .then(() => writeMirrored(uid, locale))
      .catch(() => log('locale', 'failed to mirror app language'))
  }, [signedInUid, locale])
}
